import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import GaaBallIcon from "../components/GaaBall";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success("Welcome to the sideline.");
      nav("/teams");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      {/* Visual side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/5b928005-80ef-4492-8fe9-a327e4efb087/images/034331a4ef5e2c87333ed6e719631b2953b24e0f3851f8510d09be208e5601bc.jpeg"
          alt="Gaelic football pitch"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <GaaBallIcon size={22} />
            </div>
            <span className="font-display font-extrabold text-xl tracking-tight">GaaPlay</span>
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
              See the tactic<br />from every angle.
            </h1>
            <p className="mt-4 text-muted-foreground max-w-md text-sm">
              Design the play from above, animate it, then step onto the pitch and watch the exact same
              sequence through any player's eyes.
            </p>
            <div className="mt-6 flex gap-6 font-mono2 text-xs text-primary/90">
              <span>OVERHEAD</span><span>·</span><span>2.5D PLAYER VIEW</span><span>·</span><span>SPLIT</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <GaaBallIcon size={22} />
            </div>
            <span className="font-display font-extrabold text-lg">GaaPlay</span>
          </div>

          <div className="flex gap-1 p-1 bg-secondary rounded-lg mb-8 w-fit">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => setMode(m)}
                className={`px-4 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors ${
                  mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>

          <h2 className="font-display text-2xl font-bold mb-1">
            {mode === "login" ? "Coach sign in" : "Join the sideline"}
          </h2>
          <p className="text-muted-foreground text-sm mb-6">
            {mode === "login" ? "Access your team's playbook." : "Start designing tactics in minutes."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input
                  data-testid="register-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="mt-1.5"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="coach@club.ie"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1.5"
              />
            </div>
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 font-semibold group"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <div className="mt-6 p-3 rounded-lg bg-secondary/60 border border-border text-xs text-muted-foreground">
            <span className="text-foreground font-semibold">Try the demo:</span> new accounts auto-join
            <span className="font-mono2 text-primary"> Emerald Rovers</span> with the sample play
            <span className="text-foreground"> "Midfielder Creates Space"</span>.
          </div>
        </div>
      </div>
    </div>
  );
}
